<template>
  <v-menu-wrapper
    v-model="menuOpen"
    :close-on-content-click="false"
    offset-y
    offset-overflow
    left
    content-class="screen-view-menu"
  >
    <template
      #activator="{ on, attrs }"
    >
      <div
        class="mr-2"
        v-bind="attrs"
        v-on="on"
      >
        <universal-button
          type="base"
          :label="screenLabel"
          append-icon="mdi-chevron-down"
        />
      </div>
    </template>

    <v-list-wrapper
      flat
      dense
    >
      <v-list-item-wrapper
        v-for="option in options"
        :key="option.value"
        @click="selectView(option.value)"
      >
        <v-list-item-title-wrapper>
          {{ option.label }}
        </v-list-item-title-wrapper>
      </v-list-item-wrapper>
    </v-list-wrapper>
  </v-menu-wrapper>
</template>

<script setup>
import { computed, ref } from 'vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import VMenuWrapper from '@/global/components/vuetifyComponentsWrappers/VMenuWrapper.vue'
import VListWrapper from '@/global/components/vuetifyComponentsWrappers/VListWrapper.vue'
import VListItemWrapper from '@/global/components/vuetifyComponentsWrappers/VListItemWrapper.vue'
import VListItemTitleWrapper from '@/global/components/vuetifyComponentsWrappers/VListItemTitleWrapper.vue'
import { useTranslate } from '@/global/composables/useTranslate'

// Nothing about the views themselves is decided here - `useScreenView` owns them and
// hands them over ready to be listed. Importing them instead would close a cycle, the
// composable being what imports this component.
const props = defineProps({
  // The view that is on, as the `value` of one of the options below.
  view: {
    type: String,
    default: ''
  },
  // What this screen offers, in the order it is listed: `{ value, label }` each.
  options: {
    type: Array,
    default: () => ([])
  },
  // Passed in automatically by DataTable to every `filters_row_end_components` entry -
  // unused here, declared only so it does not fall through onto the root element.
  tableData: {
    type: Array,
    default: () => ([])
  }
})

const emit = defineEmits(['select'])

const { translate } = useTranslate()

const menuOpen = ref(false)

// "Screen 2/3", the way the design names it - the fraction alone is what is picked
// between, so the word is only on the button.
const screenLabel = computed(() => {
  const label = translate('base.screen')
  const { label: viewLabel } = props.options.find(option => option.value === props.view) || {}

  return `${label} ${viewLabel || ''}`.trim()
})

const selectView = (view) => {
  emit('select', view)
  menuOpen.value = false
}
</script>

<style>
/* Unscoped: v-menu-wrapper teleports its content outside this component's scoped DOM
   tree, so a scoped rule here could never reach it. Leaflet's own panes sit at
   z-index ~400-700 inside their own stacking context, which can trap Vuetify's
   default menu z-index underneath the map - force it above. */
.screen-view-menu {
  z-index: 9999 !important;
}
</style>
